import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useTranslation } from 'react-i18next'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { QuickAddCard } from '../QuickAddCard'
import type { TaskEntryActions } from '../TaskEntry'
import { dropdownLayerOpen, targetInDropdownLayer } from '../TaskEntry/dropdownLayer'
import { useQuickAddSession, type QuickAddCreated } from '../../hooks/useQuickAddSession'
import { showQuickAddCreatedToast } from '../../utils/quickAddCreatedToast'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { getOpenProjectId } from '@/src/renderer/lib/openProject'
import './QuickAddDialog.scss'

export interface QuickAddDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Der Bereich, den die App gerade zeigt (`useLifeSpaces`); „Alle“ und Unbekanntes werden Persönlich. */
  lifeSpace: string
}

const LIFE_SPACES: readonly LifeSpace[] = ['personal', 'work', 'home']

/**
 * **Die Schnelleingabe im Web** (#234, Schritt 0b) — die Dialog-Hülle um `QuickAddCard`, geöffnet
 * mit `Q`. Auf dem Desktop öffnet `Q` stattdessen das eigene Fenster (`QuickAddWindow`).
 *
 * **Nicht modal**, ohne Schleier (Sven, 05.10.2026: die App bleibt sichtbar wie am Desktop). Ein
 * modaler Dialog sperrte alles außerhalb für Zeiger und
 * Fokus — auch die Kästen von Wann, Tags und Dauer, die als Portal am `body` hängen. Klick daneben,
 * Esc und Fokus kommen trotzdem von Radix; ein Klick in einen solchen Kasten zählt nicht als daneben.
 *
 * **Daneben klicken behält den Entwurf** (F3): Die Karte bleibt nach dem ersten Öffnen eingehängt
 * und wird nur ausgeblendet. Esc und „Abbrechen“ verwerfen ihn.
 */
export const QuickAddDialog: React.FC<QuickAddDialogProps> = ({ open, onOpenChange, lifeSpace: appLifeSpace }) => {
  const { t } = useTranslation()
  const actionsRef = useRef<TaskEntryActions | null>(null)
  // Erst beim ersten Öffnen einhängen — vorher gibt es keinen Entwurf, und die Titelzeile holte sich
  // beim Laden der App den Fokus.
  const [mounted, setMounted] = useState(open)
  useEffect(() => { if (open) setMounted(true) }, [open])

  const close = useCallback(() => onOpenChange(false), [onOpenChange])
  const startLifeSpace = useCallback(
    (): LifeSpace => LIFE_SPACES.find(space => space === appLifeSpace) ?? 'personal',
    [appLifeSpace],
  )
  /*
    Wo die Aufgabe gelandet ist, mit Sprung dorthin (Sven, 04.10.2026) — im Web ist die App ja vorn, also
    hat „Anzeigen“ den Fokus. Nach ⇧↵ springt der Dialog gleich selbst hin (`.claude/tasks/quick-add-reveal.md`).
  */
  const announce = useCallback(({ task, details, reveal }: QuickAddCreated) => {
    if (reveal) dispatchAppEvent('open-task', { task })
    else showQuickAddCreatedToast(t, task, details, { focusAction: true })
  }, [t])
  const { lifeSpace, setLifeSpace, preferredProjectId, resetKey, notice, begin, noteDraft, save, discard } = useQuickAddSession({
    startLifeSpace,
    startProjectId: getOpenProjectId,
    onClose: close,
    onCreated: announce,
  })

  useEffect(() => {
    if (open) begin()
  }, [open, begin])

  if (!mounted) return null

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal forceMount>
        <Dialog.Content
          forceMount
          className="quick-add-dialog"
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => { e.preventDefault(); actionsRef.current?.focusTitle() }}
          // Ausgeblendet ist sie nur — dann gehört Esc und jeder Klick der übrigen App.
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            if (open && !dropdownLayerOpen()) discard()
          }}
          onInteractOutside={(e) => {
            if (!open || targetInDropdownLayer(e.target)) e.preventDefault()
          }}
        >
          <Dialog.Title className="quick-add-dialog__title">{t('quickAdd.title')}</Dialog.Title>
          <QuickAddCard
            lifeSpace={lifeSpace}
            onLifeSpaceChange={setLifeSpace}
            onSubmit={(data) => void save(data)}
            onSubmitAndContinue={(data) => void save(data, 'keepOpen')}
            onSubmitAndReveal={(data) => void save(data, 'reveal')}
            onCancel={discard}
            resetKey={resetKey}
            notice={notice}
            actionsRef={actionsRef}
            preferredProjectId={preferredProjectId}
            onDraftChange={noteDraft}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
